import React, { useMemo, useState, useEffect } from 'react';
import { gridDisk } from 'h3-js';
import { Button, Input } from '../../../ui';
import type { Cluster, GridCell } from '../hooks/useClusters';

/** score cluster by how many selected cells are in or adjacent to it; used to auto-pick merge target */
function scoreClusterForSelection(cluster: Cluster, selectedCells: Set<string>): number {
    const touchSet = new Set<string>();
    for (const cell of cluster.h3Cells) {
        touchSet.add(cell);
        const ring = gridDisk(cell, 1);
        ring.forEach((c) => touchSet.add(c));
    }
    let score = 0;
    selectedCells.forEach((cell) => {
        if (touchSet.has(cell)) score++;
    });
    return score;
}

export interface CellMergePanelProps {
    selectedCells: Set<string>;
    onClearSelection: () => void;
    clusters: Cluster[];
    selectedClusterId: string | null;
    grid: GridCell[];
    onMerge: (targetClusterId: string) => Promise<void>;
    onCreate: (body: { name: string; h3Cells: string[]; city: string; country: string }) => Promise<void>;
    isMerging?: boolean;
    /** when user picks a target cluster in the dropdown, report it so the map can highlight that cluster's cells */
    onTargetClusterChange?: (clusterId: string | null) => void;
}

const CellMergePanel: React.FC<CellMergePanelProps> = ({
    selectedCells,
    onClearSelection,
    clusters,
    selectedClusterId,
    grid,
    onMerge,
    onCreate,
    isMerging = false,
    onTargetClusterChange
}) => {
    const [targetClusterId, setTargetClusterId] = useState<string>(selectedClusterId || '');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createCity, setCreateCity] = useState('');
    const [createCountry, setCreateCountry] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const cellList = useMemo(() => Array.from(selectedCells), [selectedCells]);
    const gridByCell = useMemo(() => {
        const m = new Map<string, GridCell>();
        grid.forEach((g) => m.set(g.cell, g));
        return m;
    }, [grid]);

    const { claimed, unclaimed } = useMemo(() => {
        let claimed = 0;
        let unclaimed = 0;
        cellList.forEach((cell) => {
            const g = gridByCell.get(cell);
            if (g?.clusterId) claimed++;
            else unclaimed++;
        });
        return { claimed, unclaimed };
    }, [cellList, gridByCell]);

    const activeClusters = useMemo(
        () => clusters.filter((c) => c.status !== 'archived'),
        [clusters]
    );

    const suggestedTargetId = useMemo(() => {
        if (selectedCells.size === 0 || activeClusters.length === 0) return null;
        let best: { id: string; score: number } | null = null;
        for (const c of activeClusters) {
            const score = scoreClusterForSelection(c, selectedCells);
            if (score === 0) continue;
            if (!best || score > best.score) {
                best = { id: c._id, score };
            } else if (score === best.score) {
                if (c._id === selectedClusterId) best = { id: c._id, score };
                else if (best.id !== selectedClusterId && c._id < best.id) best = { id: c._id, score };
            }
        }
        return best?.id ?? null;
    }, [activeClusters, selectedCells, selectedClusterId]);

    useEffect(() => {
        if (suggestedTargetId) setTargetClusterId(suggestedTargetId);
    }, [suggestedTargetId]);

    useEffect(() => {
        onTargetClusterChange?.(targetClusterId || null);
    }, [targetClusterId, onTargetClusterChange]);

    const handleMerge = async () => {
        if (!targetClusterId) return;
        await onMerge(targetClusterId);
        onClearSelection();
    };

    const handleCreate = async () => {
        if (!createName.trim() || !createCity.trim() || !createCountry.trim()) return;
        setIsCreating(true);
        try {
            await onCreate({
                name: createName.trim(),
                h3Cells: cellList,
                city: createCity.trim(),
                country: createCountry.trim()
            });
            setShowCreateForm(false);
            setCreateName('');
            setCreateCity('');
            setCreateCountry('');
            onClearSelection();
        } finally {
            setIsCreating(false);
        }
    };

    if (selectedCells.size === 0) return null;

    return (
        <div className="p-4 border border-ink/15 rounded-lg bg-paper flex flex-col gap-4">
            <div>
                <h4 className="font-semibold">Merge selected cells</h4>
                <p className="text-sm text-muted-foreground mt-1">
                    {selectedCells.size} cell{selectedCells.size !== 1 ? 's' : ''} selected
                    {claimed > 0 && (
                        <span className="block mt-0.5">
                            {claimed} in a cluster, {unclaimed} unclaimed
                        </span>
                    )}
                </p>
            </div>

            <div>
                <label className="text-xs text-muted-foreground block mb-1">Merge into cluster</label>
                <select
                    value={targetClusterId}
                    onChange={(e) => {
                        const v = e.target.value;
                        setTargetClusterId(v);
                        onTargetClusterChange?.(v || null);
                    }}
                    className="w-full h-10 rounded border border-ink/30 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    <option value="">Choose cluster...</option>
                    {activeClusters.map((c) => (
                        <option key={c._id} value={c._id}>
                            {c.name} ({c.totalShops} shops)
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-2">
                <Button
                    size="sm"
                    className="w-full"
                    onClick={handleMerge}
                    disabled={isMerging || !targetClusterId}
                >
                    {isMerging ? 'Merging...' : 'Merge into cluster'}
                </Button>
                <div className="flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowCreateForm((v) => !v)}
                        disabled={isMerging}
                    >
                        {showCreateForm ? 'Cancel new' : 'Create new cluster'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={onClearSelection} disabled={isMerging}>
                        Clear selection
                    </Button>
                </div>
            </div>

            {showCreateForm && (
                <div className="pt-3 space-y-2 border-t border-ink/15">
                    <Input
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        placeholder="Cluster name"
                    />
                    <Input
                        value={createCity}
                        onChange={(e) => setCreateCity(e.target.value)}
                        placeholder="City"
                    />
                    <Input
                        value={createCountry}
                        onChange={(e) => setCreateCountry(e.target.value)}
                        placeholder="Country"
                    />
                    <Button
                        size="sm"
                        onClick={handleCreate}
                        disabled={isCreating || !createName.trim() || !createCity.trim() || !createCountry.trim()}
                    >
                        {isCreating ? 'Creating...' : 'Create cluster'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CellMergePanel;
